import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Card, Badge, DataTable } from '../components/ui';

const ESITO_OPTIONS = {
  100000000: { label: 'Positivo', variant: 'success', icon: '✅' },
  100000001: { label: 'Negativo', variant: 'danger', icon: '❌' },
  100000002: { label: 'In sospeso', variant: 'warning', icon: '⏳' },
  100000003: { label: 'Da rifare', variant: 'info', icon: '🔄' }
};

const VALIDAZIONE_OPTIONS = {
  true: { label: 'Validato', variant: 'success' },
  false: { label: 'In attesa', variant: 'warning' }
};

function RapportiniPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const [rapportini, setRapportini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);

  // Filtri
  const [filters, setFilters] = useState({
    rsaId: '',
    fornitoreId: '',
    validato: '',
    esito: '',
    dataInizio: '',
    dataFine: '',
    search: ''
  });

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const isDirettoreRsa = user?.ruolo === 100000003;
  const isFornitore = user?.ruolo === 100000005;
  const canSeeAll = isAdmin || isRspp || isDirezione;

  // Carica liste per filtri
  useEffect(() => {
    const fetchLists = async () => {
      try {
        if (canSeeAll || isDirettoreRsa) {
          const rsaResult = await apiRequest('/rsa');
          setRsaList(rsaResult.value || []);
        }

        if (canSeeAll) {
          const fornitoriResult = await apiRequest('/fornitori');
          setFornitoriList(fornitoriResult.value || []);
        }
      } catch (error) {
        console.error('Error fetching lists:', error);
      }
    };

    fetchLists();
  }, [apiRequest, canSeeAll, isDirettoreRsa]);

  // Carica rapportini (interventi con documenti)
  useEffect(() => {
    const fetchRapportini = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();

        if (filters.rsaId) params.append('rsaId', filters.rsaId);
        if (filters.fornitoreId) params.append('fornitoreId', filters.fornitoreId);
        if (filters.validato !== '') params.append('validato', filters.validato);
        if (filters.esito) params.append('esito', filters.esito);
        if (filters.dataInizio) params.append('dataInizio', filters.dataInizio);
        if (filters.dataFine) params.append('dataFine', filters.dataFine);
        if (filters.search) params.append('search', filters.search);

        // Carica interventi (che contengono il link al rapportino)
        const result = await apiRequest(`/interventi?${params.toString()}`);

        // Filtra solo quelli con rapportino allegato
        const interventiConRapportino = (result.value || []).filter(
          i => i.urlRapportino || i.documentoId
        );

        setRapportini(interventiConRapportino);
      } catch (error) {
        console.error('Error fetching rapportini:', error);
        showToast('Errore nel caricamento dei rapportini', 'error');
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchRapportini, 300);
    return () => clearTimeout(debounce);
  }, [apiRequest, filters, showToast]);

  const handleDownload = async (intervento) => {
    try {
      if (intervento.urlRapportino) {
        window.open(intervento.urlRapportino, '_blank');
      } else if (intervento.documentoId) {
        const result = await apiRequest(`/documenti/${intervento.documentoId}/download`);
        if (result.downloadUrl) {
          window.open(result.downloadUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error downloading:', error);
      showToast('Errore nel download del rapportino', 'error');
    }
  };

  const handlePreview = async (intervento) => {
    try {
      if (intervento.urlRapportino) {
        // Apri direttamente se è un URL SharePoint
        window.open(intervento.urlRapportino, '_blank');
      } else if (intervento.documentoId) {
        const result = await apiRequest(`/documenti/${intervento.documentoId}/preview`);
        const previewUrl = result.previewUrl || result.url;
        if (previewUrl) {
          window.open(previewUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error getting preview:', error);
      showToast('Errore nel caricamento dell\'anteprima', 'error');
    }
  };

  const columns = [
    {
      key: 'dataIntervento',
      label: 'Data',
      render: (row) => row.dataIntervento
        ? new Date(row.dataIntervento).toLocaleDateString('it-IT')
        : '-'
    },
    {
      key: 'nome',
      label: 'Intervento',
      render: (row) => (
        <Link
          to={`/interventi/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      )
    },
    {
      key: 'rsaNome',
      label: 'RSA',
      render: (row) => row.rsaNome || row.rsa?.nome || '-'
    },
    ...(canSeeAll ? [{
      key: 'fornitoreNome',
      label: 'Fornitore',
      render: (row) => row.fornitoreNome || row.fornitore?.nome || '-'
    }] : []),
    {
      key: 'tipologia',
      label: 'Tipologia',
      render: (row) => row.tipologiaNome || row.tipologiaIntervento?.nome || '-'
    },
    {
      key: 'esito',
      label: 'Esito',
      render: (row) => {
        const esito = ESITO_OPTIONS[row.esito];
        return esito ? (
          <Badge variant={esito.variant}>
            {esito.icon} {esito.label}
          </Badge>
        ) : '-';
      }
    },
    {
      key: 'validato',
      label: 'Stato',
      render: (row) => {
        const stato = VALIDAZIONE_OPTIONS[row.validato];
        return stato ? (
          <Badge variant={stato.variant}>{stato.label}</Badge>
        ) : '-';
      }
    },
    {
      key: 'azioni',
      label: 'Rapportino',
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePreview(row)}
            title="Visualizza rapportino"
          >
            👁️
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDownload(row)}
            title="Scarica rapportino"
          >
            ⬇️
          </Button>
        </div>
      )
    }
  ];

  // Statistiche
  const stats = {
    totale: rapportini.length,
    validati: rapportini.filter(r => r.validato).length,
    inAttesa: rapportini.filter(r => !r.validato).length,
    positivi: rapportini.filter(r => r.esito === 100000000).length
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapportini Manutenzione</h1>
          <p className="text-gray-600 mt-1">
            Consulta i rapportini degli interventi di manutenzione
          </p>
        </div>
        {isFornitore && (
          <Link to="/interventi/nuovo">
            <Button>+ Nuovo Intervento</Button>
          </Link>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              📄
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totale}</p>
              <p className="text-sm text-gray-500">Totale</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              ✅
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.validati}</p>
              <p className="text-sm text-gray-500">Validati</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
              ⏳
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.inAttesa}</p>
              <p className="text-sm text-gray-500">In attesa</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
              👍
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.positivi}</p>
              <p className="text-sm text-gray-500">Esito positivo</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtri */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Cerca intervento..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="input flex-1 min-w-[200px]"
          />

          {(canSeeAll || isDirettoreRsa) && (
            <select
              value={filters.rsaId}
              onChange={(e) => setFilters(f => ({ ...f, rsaId: e.target.value }))}
              className="input w-48"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map(rsa => (
                <option key={rsa.id} value={rsa.id}>{rsa.nome}</option>
              ))}
            </select>
          )}

          {canSeeAll && (
            <select
              value={filters.fornitoreId}
              onChange={(e) => setFilters(f => ({ ...f, fornitoreId: e.target.value }))}
              className="input w-48"
            >
              <option value="">Tutti i fornitori</option>
              {fornitoriList.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          )}

          <select
            value={filters.validato}
            onChange={(e) => setFilters(f => ({ ...f, validato: e.target.value }))}
            className="input w-40"
          >
            <option value="">Tutti gli stati</option>
            <option value="true">Validati</option>
            <option value="false">In attesa</option>
          </select>

          <select
            value={filters.esito}
            onChange={(e) => setFilters(f => ({ ...f, esito: e.target.value }))}
            className="input w-40"
          >
            <option value="">Tutti gli esiti</option>
            {Object.entries(ESITO_OPTIONS).map(([value, info]) => (
              <option key={value} value={value}>
                {info.icon} {info.label}
              </option>
            ))}
          </select>
        </div>

        {/* Filtri data */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Da:</label>
            <input
              type="date"
              value={filters.dataInizio}
              onChange={(e) => setFilters(f => ({ ...f, dataInizio: e.target.value }))}
              className="input w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">A:</label>
            <input
              type="date"
              value={filters.dataFine}
              onChange={(e) => setFilters(f => ({ ...f, dataFine: e.target.value }))}
              className="input w-40"
            />
          </div>

          {Object.values(filters).some(v => v !== '') && (
            <Button
              variant="outline"
              onClick={() => setFilters({
                rsaId: '',
                fornitoreId: '',
                validato: '',
                esito: '',
                dataInizio: '',
                dataFine: '',
                search: ''
              })}
            >
              Pulisci filtri
            </Button>
          )}
        </div>
      </Card>

      {/* Tabella */}
      <Card>
        <DataTable
          data={rapportini}
          columns={columns}
          loading={loading}
          searchable={false}
          emptyMessage="Nessun rapportino trovato"
          emptyIcon="📋"
        />
      </Card>

      {/* Info per fornitori */}
      {isFornitore && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <h3 className="font-medium text-blue-900">Come caricare un rapportino</h3>
              <p className="text-sm text-blue-700 mt-1">
                Per caricare un nuovo rapportino, vai su{' '}
                <Link to="/interventi/nuovo" className="underline font-medium">
                  Nuovo Intervento
                </Link>
                {' '}e allega il file PDF nella sezione "Rapportino".
                Il rapportino sarà visibile dopo la validazione da parte del responsabile RSA.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RapportiniPage;
